import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import img011v2 from '../assets/011v2.jpg';
import img001 from '../assets/001.jpg';
import img003 from '../assets/003.jpg';
import img004 from '../assets/004.jpg';
import img005 from '../assets/005.jpg';
import img006 from '../assets/006.jpg';
import img007 from '../assets/007.jpg';
import img002 from '../assets/002.jpg';
import img009 from '../assets/009.jpg';
import img010 from '../assets/010.jpg';
import img080 from '../assets/080.jpg';

const PAGE_IDS = ['001','002','003','004','005','006','007','008','009','010','080'];
const PAGE_IMAGES = [
  img011v2, img001, img003, img004, img005, img006, img007, img002, img009, img010, img080
];

const FLIP_DURATION = 700; // ms

const ALLIANCES = [
  {
    category: 'Booking & Travel',
    desc: 'Strong alliances with OTAs, travel agencies, and corporate travel managers ensure global visibility and seamless guest access.',
    items: ['Booking.com', 'Expedia', 'TUI', 'Corporate Travel', 'Private Jet Charter', 'Yacht Services']
  },
  {
    category: 'Marketing & Brand Collaborations',
    desc: 'We work with hotel associations, luxury brands, and influencers to elevate our image and reach.',
    items: ['Global PR Agencies', 'Luxury Brand Partners', 'Influencer Network', 'Hotel Associations']
  },
  {
    category: 'Technology & Innovation',
    desc: 'Our operations are powered by top-tier PMS, CRM, and revenue management systems.',
    items: ['AI Wellness Platforms', 'PMS & CRM Systems', 'Revenue Management', 'Biometric Technology']
  },
  {
    category: 'Operations & Guest Services',
    desc: 'From housekeeping and catering to spa and wellness providers, our partners deliver flawless daily service.',
    items: ['5-Star Hospitality Training', 'Executive Chef Network', 'Spa & Wellness Experts', 'Security & Privacy']
  },
  {
    category: 'Finance & Legal',
    desc: 'Trusted advisors in banking, insurance, and legal services safeguard our business and support strategic growth.',
    items: ['Investment Advisory', 'International Legal', 'Banking Partners', 'Asset Management']
  },
  {
    category: 'Premium Experiences',
    desc: 'We team up with renowned wellness brands, event planners, and design collaborators to create unforgettable guest moments.',
    items: ['Wellness Brand Partners', 'Event Planners', 'Private Art Curators', 'VIP Membership Clubs']
  },
];

export const DigitalBook = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [flipClass, setFlipClass] = useState('');
  const [enterClass, setEnterClass] = useState('');
  const [pendingPage, setPendingPage] = useState(null);
  const touchStartX = useRef(null);
  const totalPages = PAGE_IDS.length;

  const navigate = useCallback((targetPage) => {
    if (animating || targetPage === currentPage) return;
    if (targetPage < 0 || targetPage >= totalPages) return;

    const dir = targetPage > currentPage ? 'next' : 'prev';
    setPendingPage(targetPage);
    setAnimating(true);

    // Phase 1: exit animation
    setFlipClass(dir === 'next' ? 'flip-exit-left' : 'flip-exit-right');
    setEnterClass(dir === 'next' ? 'flip-enter-right-pre' : 'flip-enter-left-pre');

    setTimeout(() => {
      // Phase 2: switch page + entry animation
      setCurrentPage(targetPage);
      setFlipClass('');
      setEnterClass(dir === 'next' ? 'flip-enter-right' : 'flip-enter-left');

      setTimeout(() => {
        setEnterClass('');
        setAnimating(false);
        setPendingPage(null);
      }, FLIP_DURATION);
    }, FLIP_DURATION / 2);
  }, [animating, currentPage, totalPages]);

  const goNext = () => navigate(currentPage + 1);
  const goPrev = () => navigate(currentPage - 1);

  // Touch/swipe support
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const pid = PAGE_IDS[currentPage];
  const p = t(`book.pages.${pid}`, { returnObjects: true });
  const img = PAGE_IMAGES[currentPage];
  const isAlliances = pid === '080';

  return (
    <section className="book-section" id="book" data-testid="digital-book-section">
      <div className="container">
        <div className="book-header">
          <p className="section-label">
            <BookOpen size={14} style={{ display: 'inline', marginRight: 6 }} />
            {t('book.title')}
          </p>
          <h2>{t('book.title')}</h2>
          <p>{t('book.subtitle')}</p>
        </div>

        <div className="book-stage">
          {/* 3D perspective wrapper */}
          <div className="book-perspective">
            <div
              className="book-container"
              data-testid="book-container"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <div className={`book-page active ${flipClass} ${enterClass}`}>
                <div className="book-page-visual">
                  <img src={img} alt={p.title || ''} loading="lazy" />
                  <div className="book-page-visual-overlay" />
                </div>
                <div className="book-page-content">
                  <p className="book-page-tag">{p.tag}</p>
                  <h2 className="book-page-title">{p.title}</h2>
                  {p.subtitle && <p className="book-page-subtitle">{p.subtitle}</p>}

                  {isAlliances ? (
                    <div style={{ overflowY: 'auto', maxHeight: '340px', paddingRight: '6px' }}>
                      {ALLIANCES.map((al, ai) => (
                        <div key={ai} style={{ marginBottom: 18 }}>
                          <p style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--color-gold)', marginBottom: 4 }}>
                            {al.category}
                          </p>
                          <p style={{ fontSize: '0.75rem', color: 'var(--color-text-dim)', marginBottom: 8, lineHeight: 1.5 }}>
                            {al.desc}
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {al.items.map((item, ii) => (
                              <span key={ii} style={{
                                padding: '3px 10px',
                                background: 'rgba(212,175,55,0.07)',
                                border: '1px solid rgba(212,175,55,0.18)',
                                borderRadius: '20px',
                                fontSize: '0.7rem',
                                color: 'var(--color-text-muted)'
                              }}>{item}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ overflowY: 'auto', maxHeight: '340px', paddingRight: '4px' }}>
                      {p.body && <p className="book-page-body">{p.body}</p>}
                      {p.body2 && <p className="book-page-body" style={{ marginTop: '10px' }}>{p.body2}</p>}
                      {(p.meta_location || p.meta_protocols || p.meta_philosophy) && (
                        <div className="book-page-meta">
                          {p.meta_location && <div className="book-page-meta-item"><strong>Location:</strong> {p.meta_location}</div>}
                          {p.meta_established && <div className="book-page-meta-item"><strong>Est.:</strong> {p.meta_established}</div>}
                          {p.meta_philosophy && <div className="book-page-meta-item"><strong>Philosophy:</strong> {p.meta_philosophy}</div>}
                          {p.meta_approach && <div className="book-page-meta-item"><strong>Approach:</strong> {p.meta_approach}</div>}
                          {p.meta_proximity && <div className="book-page-meta-item"><strong>Proximity:</strong> {p.meta_proximity}</div>}
                          {p.meta_climate && <div className="book-page-meta-item"><strong>Climate:</strong> {p.meta_climate}</div>}
                          {p.meta_unique && <div className="book-page-meta-item"><strong>Unique:</strong> {p.meta_unique}</div>}
                          {p.meta_waterfront && <div className="book-page-meta-item"><strong>Waterfront:</strong> {p.meta_waterfront}</div>}
                          {p.meta_area && <div className="book-page-meta-item"><strong>Area:</strong> {p.meta_area}</div>}
                          {p.meta_design && <div className="book-page-meta-item"><strong>Design:</strong> {p.meta_design}</div>}
                          {p.meta_tech && <div className="book-page-meta-item"><strong>Technology:</strong> {p.meta_tech}</div>}
                          {p.meta_rooms && <div className="book-page-meta-item"><strong>Rooms:</strong> {p.meta_rooms}</div>}
                          {p.meta_style && <div className="book-page-meta-item"><strong>Style:</strong> {p.meta_style}</div>}
                          {p.meta_setting && <div className="book-page-meta-item"><strong>Setting:</strong> {p.meta_setting}</div>}
                          {p.meta_protocols && <div className="book-page-meta-item"><strong>Protocols:</strong> {p.meta_protocols}</div>}
                          {p.meta_result && <div className="book-page-meta-item"><strong>Result:</strong> {p.meta_result}</div>}
                        </div>
                      )}
                      {p.quote && <div className="book-page-quote">{p.quote}</div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="book-nav">
            <button
              className="book-nav-btn"
              onClick={goPrev}
              disabled={currentPage === 0 || animating}
              data-testid="digital-book-prev-page"
              aria-label={t('book.prev')}
            >
              <ChevronLeft size={16} /> {t('book.prev')}
            </button>

            <span className="book-page-indicator" data-testid="book-page-indicator">
              {t('book.page')} {currentPage + 1} {t('book.of')} {totalPages}
            </span>

            <button
              className="book-nav-btn"
              onClick={goNext}
              disabled={currentPage === totalPages - 1 || animating}
              data-testid="digital-book-next-page"
              aria-label={t('book.next')}
            >
              {t('book.next')} <ChevronRight size={16} />
            </button>
          </div>

          {/* Dots */}
          <div className="book-dots" role="tablist" aria-label="Book pages">
            {PAGE_IDS.map((id, i) => (
              <button
                key={id}
                className={`book-dot${i === currentPage ? ' active' : ''}`}
                onClick={() => navigate(i)}
                aria-label={`${t('book.page')} ${i + 1}`}
                role="tab"
                aria-selected={i === currentPage}
                disabled={animating}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
